import { ArrowUp, AudioLines, Loader2, Mic, MousePointer2, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { setCompanionActive, useCompanionActive } from '@/features/companion/companion-active-store'
import { useVoiceStore } from '@/stores/voice-store'
import { voiceControls } from './voice/use-voice-session'

interface CompanionSpeakSendProps {
  /** Non-empty input morphs Speak into Send (reference design). */
  hasText: boolean
  canSend: boolean
  onSubmit: () => void
  /** Where a finished dictation lands (append into the textarea). */
  onDictationFinal: (text: string) => void
}

/**
 * The composer's right-side voice/send cluster:
 *  - empty input → mic (dictation toggle) + "Speak" pill (voice session)
 *  - typing      → the pill morphs into the round Send arrow
 *  - dictating   → the mic pulses with the live level; click again to finish
 * The voice SESSION swaps the whole controls row — see
 * CompanionVoiceSessionRow below.
 */
export function CompanionSpeakSend({
  hasText,
  canSend,
  onSubmit,
  onDictationFinal,
}: CompanionSpeakSendProps) {
  const status = useVoiceStore((s) => s.status)
  const audioLevel = useVoiceStore((s) => s.audioLevel)

  if (hasText) {
    return (
      <button
        type="button"
        onClick={onSubmit}
        disabled={!canSend}
        aria-label="Send message"
        className={cn(
          'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
          'bg-primary text-primary-foreground transition-colors hover:bg-primary/90',
          'disabled:cursor-not-allowed disabled:opacity-40',
        )}
      >
        <ArrowUp size={14} />
      </button>
    )
  }

  const dictating = status === 'dictating'
  return (
    <>
      <button
        type="button"
        onClick={() => voiceControls.toggleDictation(onDictationFinal)}
        aria-label={dictating ? 'Stop dictation' : 'Dictate'}
        title={dictating ? 'Stop dictation' : 'Dictate into the message box'}
        className={cn(
          'flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors',
          dictating
            ? 'bg-primary/15 text-primary'
            : 'text-muted-foreground hover:bg-hover hover:text-foreground',
        )}
      >
        <Mic size={13} style={dictating ? { opacity: 0.5 + audioLevel * 0.5 } : undefined} />
      </button>
      <button
        type="button"
        onClick={() => voiceControls.startVoiceSession()}
        disabled={dictating}
        aria-label="Speak"
        title="Voice conversation"
        className={cn(
          'flex h-6 shrink-0 items-center gap-1 rounded-full bg-primary px-2.5 text-xs font-medium',
          'text-primary-foreground transition-colors hover:bg-primary/90',
          'disabled:cursor-not-allowed disabled:opacity-40',
        )}
      >
        <AudioLines size={12} />
        Speak
      </button>
    </>
  )
}

/**
 * On/off for the companion: while on, the buddy cursor follows the mouse and
 * holding Ctrl+Alt (Ctrl+Option on Mac) talks. Same state as double-tapping
 * the chord. Lives outside CompanionSpeakSend so it stays put while typing.
 */
export function CompanionCursorToggle() {
  const on = useCompanionActive()
  return (
    <button
      type="button"
      onClick={() => setCompanionActive(!on)}
      aria-label="Companion cursor"
      aria-pressed={on}
      title={
        on
          ? 'Companion cursor on: hold Ctrl+Alt (Ctrl+Option on Mac) to talk. Click to turn off.'
          : 'Turn on the companion cursor (or double-tap Ctrl+Alt / Ctrl+Option)'
      }
      className={cn(
        'flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors',
        on
          ? 'bg-primary/15 text-primary'
          : 'text-muted-foreground hover:bg-hover hover:text-foreground',
      )}
    >
      <MousePointer2 size={13} />
    </button>
  )
}

const SESSION_LABEL: Record<string, string> = {
  listening: 'Listening…',
  processing: 'Thinking…',
  responding: 'Speaking…',
  idle: 'Tap the mic to talk',
}

/**
 * The voice-session chrome that replaces the composer's controls row while a
 * spoken back-and-forth is live: state label, the turn button (stop-and-send
 * while listening, talk-again otherwise), and ✕ End.
 */
export function CompanionVoiceSessionRow() {
  const sessionState = useVoiceStore((s) => s.sessionState)
  const audioLevel = useVoiceStore((s) => s.audioLevel)
  const notice = useVoiceStore((s) => s.notice)
  const interimText = useVoiceStore((s) => s.interimText)
  const listening = sessionState === 'listening'
  // A dropped-turn notice rides in the label slot until the user speaks again.
  const shownNotice = notice && !interimText.trim() ? notice : null

  return (
    <div className="flex items-center gap-2" data-testid="voice-session-row">
      <span
        className="flex items-center gap-1.5 text-xs text-muted-foreground"
        role={shownNotice ? 'status' : undefined}
      >
        {sessionState === 'processing' ? (
          <Loader2 size={12} className="animate-spin" />
        ) : (
          <AudioLines
            size={12}
            className={cn(listening && 'text-primary')}
            style={listening ? { opacity: 0.5 + audioLevel * 0.5 } : undefined}
          />
        )}
        {shownNotice ?? SESSION_LABEL[sessionState] ?? sessionState}
      </span>
      <div className="flex-1" />
      <button
        type="button"
        onClick={() => (listening ? voiceControls.stopVoiceTurn() : voiceControls.startVoiceTurn())}
        aria-label={listening ? 'Stop and send' : 'Talk'}
        className={cn(
          'flex h-6 items-center gap-1 rounded-full px-2.5 text-xs font-medium transition-colors',
          listening
            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
            : 'bg-muted text-foreground hover:bg-hover',
        )}
      >
        {listening ? <ArrowUp size={12} /> : <Mic size={12} />}
        {listening ? 'Send' : 'Talk'}
      </button>
      {/* An explicit, labeled way out of voice (Chris, 2026-10-02): a bare ✕
          here read like the window's close. */}
      <button
        type="button"
        onClick={() => voiceControls.endVoiceSession()}
        aria-label="End voice mode"
        title="End voice mode"
        className="flex h-6 shrink-0 items-center gap-1 rounded-full border border-destructive/40 px-2.5 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10"
      >
        <X size={12} />
        End voice
      </button>
    </div>
  )
}
